<template>
    <div
        class="workforce-grid-shell"
        :class="{
            'workforce-grid-shell--employee-dragging':
                draggingEmployeeId !== null
                && draggingEmployeeId !== undefined
        }"
    >
        <employee-workforce-overview
            v-if="overview"
            ref="overviewBoard"
            :groups="overviewGroups"
            :standalone-objects="overviewStandaloneObjects"
            :home-employees="homeEmployees"
            :distribution-employees="distributionEmployees"
            :rest-employees="restEmployees"
            :loupe-employees="loupeEmployees"
            :reserve-employees="reserveEmployees"
            :planned-objects="plannedObjects"
            :planned-employees-by-target="plannedEmployeesByTarget"
            :filter-employees="filterEmployees"
            :object-id="objectId"
            :movement-allowed="movementAllowed"
            :reserve-movement-allowed="reserveMovementAllowed"
            :can-move-all-objects="canMoveAllObjects"
            :mobile="mobile"
            :search-query="searchQuery"
            :dragging-employee-id="draggingEmployeeId"
            :dragging-assignment-id="draggingAssignmentId"
            :dragging-location-key="draggingLocationKey"
            :layout-drop-target-key="layoutDropTargetKey"
            :layout-drop-position="layoutDropPosition"
            :planned-move-pending="plannedMovePending"
            @open="$emit('open', $event)"
            @contextmenu="forwardContextMenu"
            @dragstart="$emit('dragstart', $event)"
            @dragend="$emit('dragend')"
            @drop="$emit('drop', $event)"
            @location-dragstart="
                startLocationDrag($event.object, $event.event, $event.scopeKey)
            "
            @location-dragend="stopLocationDrag"
            @location-dragover="
                onLocationDragOver($event.event, $event.object, $event.scopeKey)
            "
            @location-dragleave="
                onLocationDragLeave($event.event, $event.object)
            "
            @location-drop="
                onLocationDrop($event.event, $event.object, $event.scopeKey)
            "
            @location-reorder-step="
                moveLocationByStep(
                    $event.locationKey,
                    $event.step,
                    $event.scopeKey
                )
            "
        ></employee-workforce-overview>

        <template v-else>
        <section class="workforce-priority-grid" aria-label="Общие зоны">
            <employee-board-lane
                title="Дома / отдых"
                subtitle="Действующие сотрудники без назначения"
                icon="fas fa-home"
                :employees="homeEmployees"
                :target="{type: 'home', id: null}"
                :can-move="movementAllowed"
                :allow-drop="canMoveAllObjects"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                :dragging-employee-id="draggingEmployeeId"
                :dragging-assignment-id="draggingAssignmentId"
                fluid
                grid-cards
                scrollable
                collapsible
                :collapsed="priorityLaneIsCollapsed('home')"
                @open="$emit('open', $event)"
                @move="$emit('move', $event)"
                @contextmenu="forwardContextMenu"
                @dragstart="$emit('dragstart', $event)"
                @dragend="$emit('dragend')"
                @drop="$emit('drop', $event)"
                @toggle-collapsed="togglePriorityLane('home')"
            ></employee-board-lane>

            <employee-board-lane
                title="Не распределены / выбыли из планирования"
                subtitle="Назначение отсутствует или стало недоступно"
                icon="fas fa-exclamation-circle"
                :employees="distributionEmployees"
                :target="{type: 'requires_distribution', id: null}"
                :can-move="movementAllowed"
                :allow-drop="canMoveAllObjects"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                :dragging-employee-id="draggingEmployeeId"
                :dragging-assignment-id="draggingAssignmentId"
                fluid
                grid-cards
                scrollable
                collapsible
                :collapsed="priorityLaneIsCollapsed('distribution')"
                @open="$emit('open', $event)"
                @move="$emit('move', $event)"
                @contextmenu="forwardContextMenu"
                @dragstart="$emit('dragstart', $event)"
                @dragend="$emit('dragend')"
                @drop="$emit('drop', $event)"
                @toggle-collapsed="togglePriorityLane('distribution')"
            ></employee-board-lane>

            <employee-board-lane
                v-if="restEmployees.length"
                title="Отдых"
                subtitle="Сотрудников в этот период не планируют на объекты"
                icon="fas fa-umbrella-beach"
                :employees="restEmployees"
                :can-move="false"
                :allow-drop="false"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                fluid
                grid-cards
                scrollable
                collapsible
                :collapsed="priorityLaneIsCollapsed('rest')"
                @open="$emit('open', $event)"
                @contextmenu="forwardContextMenu"
                @toggle-collapsed="togglePriorityLane('rest')"
            ></employee-board-lane>

            <employee-board-lane
                v-if="loupeEmployees.length"
                title="🔍 Выбыли из планирования"
                subtitle="Временно вне планирования; состояние можно уточнить"
                icon="fas fa-search"
                :employees="loupeEmployees"
                :can-move="false"
                :allow-drop="false"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                fluid
                grid-cards
                scrollable
                collapsible
                :collapsed="priorityLaneIsCollapsed('loupe')"
                @open="$emit('open', $event)"
                @contextmenu="forwardContextMenu"
                @toggle-collapsed="togglePriorityLane('loupe')"
            ></employee-board-lane>
        </section>

        <section
            v-if="plannedObjects.length"
            class="workforce-planned-object-grid"
            aria-label="Плановые объекты"
        >
            <employee-board-lane
                v-for="item in plannedObjects"
                :key="`planned-object-${item.planned_object_id}`"
                class="workforce-planned-object-lane"
                :title="`${item.planning_code} — ${item.name}`"
                subtitle="Плановый объект · контракт не заключён"
                icon="fas fa-map-signs"
                :employees="item.employees"
                :target="{type: 'planned_object', id: item.planned_object_id}"
                :can-move="false"
                :allow-drop="false"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                fluid
                grid-cards
                scrollable
                collapsible
                @open="$emit('open', $event)"
                @contextmenu="forwardContextMenu"
            ></employee-board-lane>
        </section>

        <section
            v-if="reserveExpanded || searchIsActive"
            class="workforce-reserve-tray"
            aria-label="Резерв сотрудников"
        >
            <header class="workforce-reserve-tray__header">
                <div>
                    <i class="fas fa-archive" aria-hidden="true"></i>
                    <strong>Резерв</strong>
                    <span>Бывшие сотрудники для возможного повторного найма</span>
                </div>
                <button
                    v-if="!searchIsActive"
                    type="button"
                    aria-label="Свернуть резерв"
                    @click="$emit('reserve-expanded', false)"
                >
                    <i class="fas fa-times" aria-hidden="true"></i>
                </button>
            </header>
            <employee-board-lane
                title="Резерв"
                :subtitle="
                    reserveMovementAllowed
                        ? 'Нажмите «Назначения» или перетащите сотрудника в нужное место'
                        : 'Возврат и назначение доступны кадровикам на доске «Сегодня»'
                "
                icon="fas fa-archive"
                :employees="reserveEmployees"
                :can-move="reserveMovementAllowed"
                :allow-reserve-move="reserveMovementAllowed"
                :allow-drop="false"
                :mobile="mobile"
                :compact="compact"
                :search-query="searchQuery"
                :dragging-employee-id="draggingEmployeeId"
                :dragging-assignment-id="draggingAssignmentId"
                fluid
                grid-cards
                scrollable
                @open="$emit('open', $event)"
                @move="$emit('move', $event)"
                @contextmenu="forwardContextMenu"
                @dragstart="$emit('dragstart', $event)"
                @dragend="$emit('dragend')"
            ></employee-board-lane>
        </section>

        <div
            class="workforce-grid-workspace"
            :class="{
                'workforce-grid-workspace--scope-collapsed':
                    scopePanelIsCollapsed
            }"
        >
            <aside
                class="workforce-scope"
                :class="{
                    'workforce-scope--collapsed': scopePanelIsCollapsed
                }"
                aria-label="Навигация по объектам"
            >
                <button
                    v-if="scopePanelIsCollapsed"
                    type="button"
                    class="workforce-scope__collapsed-strip"
                    title="Развернуть область доски"
                    aria-label="Развернуть область доски"
                    :aria-expanded="false"
                    @click="$emit('scope-panel-collapsed', false)"
                >
                    <i class="fas fa-chevron-right" aria-hidden="true"></i>
                    <span>Область доски</span>
                    <strong>{{ selectedScopeObjectCount }}</strong>
                </button>

                <div v-else class="workforce-scope__heading">
                    <div>
                        <strong>Область доски</strong>
                        <span>{{ totalObjectCount }} объектов</span>
                    </div>
                    <div
                        class="workforce-scope__sorts"
                        role="group"
                        aria-label="Сортировка объектов"
                    >
                        <button
                            v-if="!mobile"
                            type="button"
                            class="workforce-scope__sort workforce-scope__collapse"
                            title="Свернуть область доски влево"
                            aria-label="Свернуть область доски влево"
                            :aria-expanded="true"
                            @click="$emit('scope-panel-collapsed', true)"
                        >
                            <i class="fas fa-chevron-left" aria-hidden="true"></i>
                        </button>
                        <button
                            type="button"
                            class="workforce-scope__sort"
                            :class="{
                                'workforce-scope__sort--active':
                                    layoutMode === 'name'
                            }"
                            :aria-pressed="layoutMode === 'name' ? 'true' : 'false'"
                            :title="locationSortTitle('name')"
                            @click="setLocationSort('name')"
                        >
                            <i class="fas fa-font" aria-hidden="true"></i>
                            <i
                                :class="locationSortDirectionIcon('name')"
                                aria-hidden="true"
                            ></i>
                        </button>
                        <button
                            type="button"
                            class="workforce-scope__sort"
                            :class="{
                                'workforce-scope__sort--active':
                                    layoutMode === 'headcount'
                            }"
                            :aria-pressed="layoutMode === 'headcount' ? 'true' : 'false'"
                            :title="locationSortTitle('headcount')"
                            @click="setLocationSort('headcount')"
                        >
                            <i class="fas fa-users" aria-hidden="true"></i>
                            <i
                                :class="locationSortDirectionIcon('headcount')"
                                aria-hidden="true"
                            ></i>
                        </button>
                    </div>
                </div>

                <b-input-group
                    v-show="!scopePanelIsCollapsed"
                    size="sm"
                    class="workforce-scope__search"
                >
                    <b-input-group-prepend is-text>
                        <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model.trim="locationQuery"
                        type="search"
                        placeholder="Найти объект"
                        aria-label="Поиск объекта или группы"
                    ></b-form-input>
                </b-input-group>

                <nav v-show="!scopePanelIsCollapsed" class="workforce-scope__nav">
                    <button
                        type="button"
                        class="workforce-scope__item workforce-scope__item--root"
                        :class="{'workforce-scope__item--active': scopeIsActive('all')}"
                        @click="selectScope('all')"
                    >
                        <span>
                            <i class="fas fa-th-large" aria-hidden="true"></i>
                            Все площадки
                        </span>
                        <strong>{{ allAssignedEmployeeCount }}</strong>
                    </button>

                    <div
                        v-if="selectedLocationCount"
                        class="workforce-scope__selection"
                    >
                        <button
                            type="button"
                            class="workforce-scope__item workforce-scope__item--selected"
                            :class="{
                                'workforce-scope__item--active':
                                    scopeIsActive('selection')
                            }"
                            @click="selectScope('selection')"
                        >
                            <span>
                                <i class="fas fa-object-group" aria-hidden="true"></i>
                                Выбранные площадки
                            </span>
                            <strong>{{ selectedLocationCount }}</strong>
                        </button>
                        <button
                            type="button"
                            class="workforce-scope__selection-clear"
                            title="Очистить выбранные площадки"
                            aria-label="Очистить выбранные площадки"
                            @click="clearObjectSelection"
                        >
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>

                    <div
                        v-for="group in navigationGroups"
                        :key="`scope-group-${group.id}`"
                        class="workforce-scope__branch"
                    >
                        <div
                            class="workforce-scope__group-row"
                            :class="{
                                'workforce-scope__group-row--selected':
                                    groupSelectionState(group) !== 'none'
                            }"
                        >
                            <button
                                type="button"
                                class="workforce-scope__item workforce-scope__item--group"
                                :class="{
                                    'workforce-scope__item--active':
                                        scopeIsActive(`group:${group.id}`)
                                }"
                                @click="selectScope(`group:${group.id}`)"
                            >
                                <span>
                                    <i class="fas fa-layer-group" aria-hidden="true"></i>
                                    {{ group.name }}
                                </span>
                                <strong>{{ groupEmployeeCount(group) }}</strong>
                            </button>
                            <button
                                type="button"
                                class="workforce-scope__group-toggle"
                                :class="{
                                    'workforce-scope__group-toggle--selected':
                                        groupSelectionState(group) !== 'none'
                                }"
                                :aria-pressed="
                                    selectionAriaPressed(groupSelectionState(group))
                                "
                                :title="groupSelectionTitle(group)"
                                @click="toggleGroupSelection(group)"
                            >
                                <i
                                    :class="selectionIcon(groupSelectionState(group))"
                                    aria-hidden="true"
                                ></i>
                                <span class="sr-only">{{ groupSelectionTitle(group) }}</span>
                            </button>
                        </div>
                        <div
                            v-for="object in group.navigationObjects"
                            :key="`scope-object-${objectId(object)}`"
                            class="workforce-scope__object-row"
                            :class="{
                                'workforce-scope__object-row--selected':
                                    objectIsSelected(object)
                            }"
                        >
                            <button
                                type="button"
                                class="workforce-scope__item workforce-scope__item--object"
                                :class="{
                                    'workforce-scope__item--active':
                                        scopeIsActive(`object:${objectId(object)}`)
                                }"
                                :title="object.name"
                                @click="selectScope(`object:${objectId(object)}`)"
                            >
                                <span>
                                    <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                                    {{ object.name }}
                                </span>
                                <strong>{{ employeeCount(object.employees) }}</strong>
                            </button>
                            <button
                                type="button"
                                class="workforce-scope__object-toggle"
                                :class="{
                                    'workforce-scope__object-toggle--selected':
                                        objectIsSelected(object)
                                }"
                                :aria-pressed="objectIsSelected(object) ? 'true' : 'false'"
                                :title="objectSelectionTitle(object)"
                                @click="toggleObjectSelection(object)"
                            >
                                <i
                                    :class="
                                        objectIsSelected(object)
                                            ? 'fas fa-check-square'
                                            : 'far fa-square'
                                    "
                                    aria-hidden="true"
                                ></i>
                                <span class="sr-only">{{ objectSelectionTitle(object) }}</span>
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="navigationStandaloneObjects.length || standaloneBranchVisible"
                        class="workforce-scope__branch"
                    >
                        <div
                            class="workforce-scope__group-row"
                            :class="{
                                'workforce-scope__group-row--selected':
                                    standaloneSelectionState !== 'none'
                            }"
                        >
                            <button
                                type="button"
                                class="workforce-scope__item workforce-scope__item--group"
                                :class="{
                                    'workforce-scope__item--active':
                                        scopeIsActive('standalone')
                                }"
                                @click="selectScope('standalone')"
                            >
                                <span>
                                    <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                                    Без группы
                                </span>
                                <strong>{{ standaloneEmployeeCount }}</strong>
                            </button>
                            <button
                                v-if="standaloneObjects.length"
                                type="button"
                                class="workforce-scope__group-toggle"
                                :class="{
                                    'workforce-scope__group-toggle--selected':
                                        standaloneSelectionState !== 'none'
                                }"
                                :aria-pressed="selectionAriaPressed(standaloneSelectionState)"
                                :title="standaloneSelectionTitle"
                                @click="toggleObjectCollectionSelection(standaloneObjects)"
                            >
                                <i
                                    :class="selectionIcon(standaloneSelectionState)"
                                    aria-hidden="true"
                                ></i>
                                <span class="sr-only">{{ standaloneSelectionTitle }}</span>
                            </button>
                        </div>
                        <div
                            v-for="object in navigationStandaloneObjects"
                            :key="`scope-standalone-${objectId(object)}`"
                            class="workforce-scope__object-row"
                            :class="{
                                'workforce-scope__object-row--selected':
                                    objectIsSelected(object)
                            }"
                        >
                            <button
                                type="button"
                                class="workforce-scope__item workforce-scope__item--object"
                                :class="{
                                    'workforce-scope__item--active':
                                        scopeIsActive(`object:${objectId(object)}`)
                                }"
                                :title="object.name"
                                @click="selectScope(`object:${objectId(object)}`)"
                            >
                                <span>
                                    <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
                                    {{ object.name }}
                                </span>
                                <strong>{{ employeeCount(object.employees) }}</strong>
                            </button>
                            <button
                                type="button"
                                class="workforce-scope__object-toggle"
                                :class="{
                                    'workforce-scope__object-toggle--selected':
                                        objectIsSelected(object)
                                }"
                                :aria-pressed="objectIsSelected(object) ? 'true' : 'false'"
                                :title="objectSelectionTitle(object)"
                                @click="toggleObjectSelection(object)"
                            >
                                <i
                                    :class="
                                        objectIsSelected(object)
                                            ? 'fas fa-check-square'
                                            : 'far fa-square'
                                    "
                                    aria-hidden="true"
                                ></i>
                                <span class="sr-only">{{ objectSelectionTitle(object) }}</span>
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="locationQuery && !navigationHasMatches"
                        class="workforce-scope__empty"
                    >
                        Объекты не найдены
                    </div>
                </nav>
            </aside>

            <section class="workforce-locations">
                <header class="workforce-locations__header">
                    <div>
                        <span class="workforce-locations__eyebrow">Текущая область</span>
                        <h2>{{ selectedScopeTitle }}</h2>
                        <p>{{ selectedScopeSubtitle }}</p>
                    </div>
                    <div class="workforce-locations__header-actions">
                        <div
                            v-if="showsLocationLayoutControls"
                            class="workforce-layout-mode"
                            aria-live="polite"
                        >
                            <span
                                class="workforce-layout-mode__status"
                                :class="{
                                    'workforce-layout-mode__status--manual':
                                        layoutMode === 'manual'
                                }"
                            >
                                <i
                                    :class="
                                        layoutMode === 'manual'
                                            ? 'fas fa-grip-lines'
                                            : locationSortModeIcon
                                    "
                                    aria-hidden="true"
                                ></i>
                                {{ locationSortModeLabel }}
                            </span>
                            <button
                                v-if="layoutMode === 'manual'"
                                type="button"
                                class="workforce-layout-mode__reset"
                                @click="resetLocationLayout"
                            >
                                <i class="fas fa-sort-amount-down" aria-hidden="true"></i>
                                Сортировать по числу
                            </button>
                            <span
                                v-else-if="!mobile"
                                class="workforce-layout-mode__hint"
                            >
                                Потяните объект за
                                <i class="fas fa-grip-lines" aria-hidden="true"></i>
                            </span>
                        </div>
                        <div class="workforce-locations__metrics">
                            <span>
                                <strong>{{ selectedScopeObjectCount }}</strong>
                                объектов
                            </span>
                            <span>
                                <strong>{{ selectedScopeEmployeeCount }}</strong>
                                сотрудников
                            </span>
                        </div>
                    </div>
                </header>

                <section
                    v-if="focusedObject"
                    class="workforce-location-section workforce-location-section--focused"
                >
                    <div class="workforce-location-grid workforce-location-grid--focused">
                        <employee-board-lane
                            class="workforce-object-lane"
                            :title="focusedObject.name"
                            :employees="filtered(focusedObject.employees)"
                            :planned-employees="filtered(
                                plannedEmployeesForTarget(
                                    'object',
                                    objectId(focusedObject)
                                )
                            )"
                            :headcount-employees="focusedObject.employees"
                            :target="{type: 'object', id: objectId(focusedObject)}"
                            :can-move="movementAllowed"
                            :allow-drop="focusedObject.can_move === true"
                            :allow-assign="focusedObject.can_move === true"
                            :mobile="mobile"
                            :compact="compact"
                            :search-query="searchQuery"
                            :dragging-employee-id="draggingEmployeeId"
                            :dragging-assignment-id="draggingAssignmentId"
                            fluid
                            grid-cards
                            scrollable
                            :planned-move-pending="plannedMovePending"
                            @open="$emit('open', $event)"
                            @move="$emit('move', $event)"
                            @contextmenu="forwardContextMenu"
                            @dragstart="$emit('dragstart', $event)"
                            @dragend="$emit('dragend')"
                            @drop="$emit('drop', $event)"
                            @confirm-planned-move="
                                $emit('confirm-planned-move', $event)
                            "
                            @assign="$emit('assign-to-target', $event)"
                        ></employee-board-lane>
                    </div>
                </section>

                <section
                    v-if="
                        usesFlatObjectGrid &&
                        (selectedFlatGroups.length || visibleFlatObjects.length)
                    "
                    class="workforce-location-section workforce-location-section--flat"
                >
                    <div class="workforce-location-grid workforce-location-grid--flat">
                        <div
                            v-for="group in selectedFlatGroups"
                            :key="`selection-group-${group.id}`"
                            class="workforce-location-tile"
                        >
                            <employee-board-lane
                                class="workforce-group-target-lane"
                                :title="group.name"
                                subtitle="На группе объектов · без уточнения объекта"
                                :employees="filtered(group.employees)"
                                :planned-employees="filtered(
                                    plannedEmployeesForTarget(
                                        'object_group',
                                        group.id
                                    )
                                )"
                                :headcount-employees="group.employees"
                                :target="{type: 'object_group', id: group.id}"
                                :can-move="movementAllowed"
                                :allow-drop="group.can_move === true"
                                :allow-assign="group.can_move === true"
                                :mobile="mobile"
                                :compact="compact"
                                :search-query="searchQuery"
                                :dragging-employee-id="draggingEmployeeId"
                                :dragging-assignment-id="draggingAssignmentId"
                                fluid
                                grid-cards
                                scrollable
                                :planned-move-pending="plannedMovePending"
                                @open="$emit('open', $event)"
                                @move="$emit('move', $event)"
                                @contextmenu="forwardContextMenu"
                                @dragstart="$emit('dragstart', $event)"
                                @dragend="$emit('dragend')"
                                @drop="$emit('drop', $event)"
                                @confirm-planned-move="
                                    $emit('confirm-planned-move', $event)
                                "
                                @assign="$emit('assign-to-target', $event)"
                            ></employee-board-lane>
                        </div>
                        <div
                            v-for="object in visibleFlatObjects"
                            :key="object.layout_key"
                            class="workforce-location-tile"
                            :class="locationTileClasses(object)"
                            @dragover="onLocationDragOver($event, object)"
                            @dragleave="onLocationDragLeave($event, object)"
                            @drop="onLocationDrop($event, object)"
                        >
                            <employee-board-lane
                                class="workforce-object-lane"
                                :title="object.name"
                                :subtitle="object.location_group_name"
                                :employees="filtered(object.employees)"
                                :planned-employees="filtered(
                                    plannedEmployeesForTarget(
                                        'object',
                                        objectId(object)
                                    )
                                )"
                                :headcount-employees="object.employees"
                                :target="{type: 'object', id: objectId(object)}"
                                :can-move="movementAllowed"
                                :allow-drop="object.can_move === true"
                                :allow-assign="object.can_move === true"
                                :mobile="mobile"
                                :compact="compact"
                                :search-query="searchQuery"
                                :dragging-employee-id="draggingEmployeeId"
                                :dragging-assignment-id="draggingAssignmentId"
                                :reorderable="!mobile && visibleFlatObjects.length > 1"
                                :reorder-label="object.name"
                                fluid
                                grid-cards
                                scrollable
                                :planned-move-pending="plannedMovePending"
                                @open="$emit('open', $event)"
                                @move="$emit('move', $event)"
                                @contextmenu="forwardContextMenu"
                                @dragstart="$emit('dragstart', $event)"
                                @dragend="$emit('dragend')"
                                @drop="$emit('drop', $event)"
                                @confirm-planned-move="
                                    $emit('confirm-planned-move', $event)
                                "
                                @assign="$emit('assign-to-target', $event)"
                                @reorder-dragstart="
                                    startLocationDrag(object, $event)
                                "
                                @reorder-dragend="stopLocationDrag"
                                @reorder-step="
                                    moveLocationByStep(object.layout_key, $event)
                                "
                            ></employee-board-lane>
                        </div>
                    </div>
                </section>

                <section
                    v-for="group in displayGroups"
                    :key="`group-${group.id}`"
                    class="
                        workforce-location-section
                        workforce-location-section--group
                    "
                    :class="{
                        'workforce-location-section--collapsed':
                            sectionIsCollapsed(`group:${group.id}`)
                    }"
                >
                    <button
                        type="button"
                        class="workforce-location-section__header"
                        :aria-expanded="
                            sectionIsCollapsed(`group:${group.id}`) ? 'false' : 'true'
                        "
                        @click="toggleSection(`group:${group.id}`)"
                    >
                        <span class="workforce-location-section__identity">
                            <i class="fas fa-layer-group" aria-hidden="true"></i>
                            <span>
                                <strong>{{ group.name }}</strong>
                                <small>
                                    {{ group.objects.length }} объектов ·
                                    {{ groupEmployeeCount(group) }} сотрудников
                                </small>
                            </span>
                        </span>
                        <i
                            :class="
                                sectionIsCollapsed(`group:${group.id}`)
                                    ? 'fas fa-chevron-down'
                                    : 'fas fa-chevron-up'
                            "
                            aria-hidden="true"
                        ></i>
                    </button>
                    <div
                        v-if="!sectionIsCollapsed(`group:${group.id}`)"
                        class="workforce-location-grid"
                    >
                        <employee-board-lane
                            class="workforce-group-target-lane"
                            title="На группе объектов"
                            subtitle="Без уточнения объекта"
                            :employees="filtered(group.employees)"
                            :planned-employees="filtered(
                                plannedEmployeesForTarget(
                                    'object_group',
                                    group.id
                                )
                            )"
                            :headcount-employees="group.employees"
                            :target="{type: 'object_group', id: group.id}"
                            :can-move="movementAllowed"
                            :allow-drop="group.can_move === true"
                            :allow-assign="group.can_move === true"
                            :mobile="mobile"
                            :compact="compact"
                            :search-query="searchQuery"
                            :dragging-employee-id="draggingEmployeeId"
                            :dragging-assignment-id="draggingAssignmentId"
                            fluid
                            grid-cards
                            scrollable
                            :planned-move-pending="plannedMovePending"
                            @open="$emit('open', $event)"
                            @move="$emit('move', $event)"
                            @contextmenu="forwardContextMenu"
                            @dragstart="$emit('dragstart', $event)"
                            @dragend="$emit('dragend')"
                            @drop="$emit('drop', $event)"
                            @confirm-planned-move="
                                $emit('confirm-planned-move', $event)
                            "
                            @assign="$emit('assign-to-target', $event)"
                        ></employee-board-lane>

                        <div
                            v-for="object in group.display_objects"
                            :key="object.layout_key"
                            class="workforce-location-tile"
                            :class="[
                                locationTileClasses(object),
                                {
                                    'workforce-location-tile--empty-toggle':
                                        object.empty_toggle === true
                                }
                            ]"
                            @dragover="
                                onLocationDragOver(
                                    $event,
                                    object,
                                    group.layout_scope_key
                                )
                            "
                            @dragleave="onLocationDragLeave($event, object)"
                            @drop="
                                onLocationDrop(
                                    $event,
                                    object,
                                    group.layout_scope_key
                                )
                            "
                        >
                            <button
                                v-if="object.empty_toggle === true"
                                type="button"
                                class="workforce-empty-objects-toggle"
                                :aria-expanded="object.expanded ? 'true' : 'false'"
                                @click.stop="toggleGroupEmptyObjects(group.id)"
                            >
                                <i
                                    class="fas"
                                    :class="object.expanded
                                        ? 'fa-eye-slash'
                                        : 'fa-eye'"
                                    aria-hidden="true"
                                ></i>
                                <span>
                                    {{ object.expanded
                                        ? 'Скрыть пустые'
                                        : 'Показать пустые' }}
                                </span>
                                <small>{{ object.empty_count }}</small>
                            </button>
                            <employee-board-lane
                                v-else
                                class="workforce-object-lane"
                                :title="object.name"
                                :employees="filtered(object.employees)"
                                :planned-employees="filtered(
                                    plannedEmployeesForTarget(
                                        'object',
                                        objectId(object)
                                    )
                                )"
                                :headcount-employees="object.employees"
                                :target="{type: 'object', id: objectId(object)}"
                                :can-move="movementAllowed"
                                :allow-drop="object.can_move === true"
                                :allow-assign="object.can_move === true"
                                :mobile="mobile"
                                :compact="compact"
                                :search-query="searchQuery"
                                :dragging-employee-id="draggingEmployeeId"
                                :dragging-assignment-id="draggingAssignmentId"
                                :reorderable="
                                    !mobile && group.visible_object_count > 1
                                "
                                :reorder-label="object.name"
                                fluid
                                grid-cards
                                scrollable
                                :planned-move-pending="plannedMovePending"
                                @open="$emit('open', $event)"
                                @move="$emit('move', $event)"
                                @contextmenu="forwardContextMenu"
                                @dragstart="$emit('dragstart', $event)"
                                @dragend="$emit('dragend')"
                                @drop="$emit('drop', $event)"
                                @confirm-planned-move="
                                    $emit('confirm-planned-move', $event)
                                "
                                @assign="$emit('assign-to-target', $event)"
                                @reorder-dragstart="
                                    startLocationDrag(
                                        object,
                                        $event,
                                        group.layout_scope_key
                                    )
                                "
                                @reorder-dragend="stopLocationDrag"
                                @reorder-step="
                                    moveLocationByStep(
                                        object.layout_key,
                                        $event,
                                        group.layout_scope_key
                                    )
                                "
                            ></employee-board-lane>
                        </div>
                    </div>
                </section>

                <section
                    v-if="visibleStandaloneObjects.length"
                    class="workforce-location-section workforce-location-section--standalone"
                    :class="{
                        'workforce-location-section--collapsed':
                            sectionIsCollapsed('standalone')
                    }"
                >
                    <button
                        type="button"
                        class="workforce-location-section__header"
                        :aria-expanded="sectionIsCollapsed('standalone') ? 'false' : 'true'"
                        @click="toggleSection('standalone')"
                    >
                        <span class="workforce-location-section__identity">
                            <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                            <span>
                                <strong>Объекты без группы</strong>
                                <small>
                                    {{ visibleStandaloneObjects.length }} объектов ·
                                    {{ standaloneEmployeeCount }} сотрудников
                                </small>
                            </span>
                        </span>
                        <i
                            :class="
                                sectionIsCollapsed('standalone')
                                    ? 'fas fa-chevron-down'
                                    : 'fas fa-chevron-up'
                            "
                            aria-hidden="true"
                        ></i>
                    </button>
                    <div
                        v-if="!sectionIsCollapsed('standalone')"
                        class="workforce-location-grid"
                    >
                        <div
                            v-for="object in visibleStandaloneObjects"
                            :key="object.layout_key"
                            class="workforce-location-tile"
                            :class="locationTileClasses(object)"
                            @dragover="
                                onLocationDragOver($event, object, 'standalone')
                            "
                            @dragleave="onLocationDragLeave($event, object)"
                            @drop="
                                onLocationDrop($event, object, 'standalone')
                            "
                        >
                            <employee-board-lane
                                class="workforce-object-lane"
                                :title="object.name"
                                :employees="filtered(object.employees)"
                                :planned-employees="filtered(
                                    plannedEmployeesForTarget(
                                        'object',
                                        objectId(object)
                                    )
                                )"
                                :headcount-employees="object.employees"
                                :target="{type: 'object', id: objectId(object)}"
                                :can-move="movementAllowed"
                                :allow-drop="object.can_move === true"
                                :allow-assign="object.can_move === true"
                                :mobile="mobile"
                                :compact="compact"
                                :search-query="searchQuery"
                                :dragging-employee-id="draggingEmployeeId"
                                :dragging-assignment-id="draggingAssignmentId"
                                :reorderable="
                                    !mobile && visibleStandaloneObjects.length > 1
                                "
                                :reorder-label="object.name"
                                fluid
                                grid-cards
                                scrollable
                                :planned-move-pending="plannedMovePending"
                                @open="$emit('open', $event)"
                                @move="$emit('move', $event)"
                                @contextmenu="forwardContextMenu"
                                @dragstart="$emit('dragstart', $event)"
                                @dragend="$emit('dragend')"
                                @drop="$emit('drop', $event)"
                                @confirm-planned-move="
                                    $emit('confirm-planned-move', $event)
                                "
                                @assign="$emit('assign-to-target', $event)"
                                @reorder-dragstart="
                                    startLocationDrag(
                                        object,
                                        $event,
                                        'standalone'
                                    )
                                "
                                @reorder-dragend="stopLocationDrag"
                                @reorder-step="
                                    moveLocationByStep(
                                        object.layout_key,
                                        $event,
                                        'standalone'
                                    )
                                "
                            ></employee-board-lane>
                        </div>
                    </div>
                </section>

                <div
                    v-if="!focusedObject &&
                        !visibleGroups.length &&
                        !visibleStandaloneObjects.length &&
                        !visibleFlatObjects.length"
                    class="workforce-locations__empty"
                >
                    <i class="fas fa-map-marked-alt" aria-hidden="true"></i>
                    <strong>В этой области нет объектов</strong>
                    <span>Выберите другую группу слева.</span>
                </div>
            </section>
        </div>
        </template>
    </div>
</template>

<script>
import EmployeeBoardLane from './EmployeeBoardLane.vue';
import EmployeeWorkforceOverview from './EmployeeWorkforceOverview.vue';
import workforceLayout from '@/utils/employeeWorkforceLayout';

export default {
    name: 'EmployeeWorkforceGrid',
    components: {
        EmployeeBoardLane,
        EmployeeWorkforceOverview
    },
    props: {
        groups: {
            type: Array,
            default: () => []
        },
        standaloneObjects: {
            type: Array,
            default: () => []
        },
        plannedObjects: {
            type: Array,
            default: () => []
        },
        homeEmployees: {
            type: Array,
            default: () => []
        },
        distributionEmployees: {
            type: Array,
            default: () => []
        },
        restEmployees: {
            type: Array,
            default: () => []
        },
        loupeEmployees: {
            type: Array,
            default: () => []
        },
        reserveEmployees: {
            type: Array,
            default: () => []
        },
        plannedEmployeesByTarget: {
            type: Object,
            default: () => ({})
        },
        filterEmployees: {
            type: Function,
            required: true
        },
        objectId: {
            type: Function,
            required: true
        },
        movementAllowed: {
            type: Boolean,
            default: false
        },
        reserveMovementAllowed: {
            type: Boolean,
            default: false
        },
        canMoveAllObjects: {
            type: Boolean,
            default: false
        },
        mobile: {
            type: Boolean,
            default: false
        },
        compact: {
            type: Boolean,
            default: true
        },
        overview: {
            type: Boolean,
            default: false
        },
        searchQuery: {
            type: String,
            default: ''
        },
        nameFormat: {
            type: String,
            default: 'short'
        },
        reserveExpanded: {
            type: Boolean,
            default: false
        },
        plannedMovePending: {
            type: Boolean,
            default: false
        },
        draggingEmployeeId: {
            type: [String, Number],
            default: null
        },
        draggingAssignmentId: {
            type: [String, Number],
            default: null
        },
        scopeResetToken: {
            type: Number,
            default: 0
        },
        scopePanelCollapsed: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            employeeNameFormatState: {value: this.nameFormat},
            selectedScopeKey: 'all',
            selectedGroupKeys: [],
            selectedObjectKeys: [],
            locationQuery: '',
            collapsedSections: {},
            collapsedPriorityLanes: {},
            layoutMode: 'headcount',
            layoutDirection: 'desc',
            manualLocationOrder: [],
            draggingLocationKey: null,
            draggingLocationScopeKey: null,
            layoutDropTargetKey: null,
            layoutDropPosition: 'before',
            expandedEmptyGroups: {}
        };
    },
    provide() {
        return {
            workforceEmployeeNameFormat: this.employeeNameFormatState
        };
    },
    computed: {
        scopePanelIsCollapsed() {
            return !this.mobile && this.scopePanelCollapsed;
        },
        searchIsActive() {
            return Boolean(String(this.searchQuery || '').trim());
        },
        totalObjectCount() {
            return this.groups.reduce((count, group) => {
                return count + (Array.isArray(group.objects) ? group.objects.length : 0);
            }, this.standaloneObjects.length);
        },
        allObjects() {
            const objects = [];
            this.groups.forEach(group => {
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    objects.push(object);
                });
            });
            this.standaloneObjects.forEach(object => objects.push(object));
            return objects;
        },
        currentUser() {
            return this.$store &&
                this.$store.getters &&
                this.$store.getters.currentUser || null;
        },
        layoutStorageKey() {
            return workforceLayout.storageKey(this.currentUser);
        },
        allObjectLocations() {
            const locations = [];
            this.groups.forEach(group => {
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    const id = this.objectId(object);
                    if (!id) return;
                    locations.push(Object.assign({}, object, {
                        layout_key: `object:${id}`,
                        location_group_name: group.name || 'Группа объектов',
                        location_scope_key: `group:${group.id}`
                    }));
                });
            });
            this.standaloneObjects.forEach(object => {
                const id = this.objectId(object);
                if (!id) return;
                locations.push(Object.assign({}, object, {
                    layout_key: `object:${id}`,
                    location_group_name: 'Без группы',
                    location_scope_key: 'standalone'
                }));
            });
            return locations;
        },
        orderedAllObjectLocations() {
            return workforceLayout.arrangeLocations(
                this.allObjectLocations,
                this.layoutMode === 'manual' ? this.manualLocationOrder : [],
                this.layoutMode,
                this.layoutDirection
            );
        },
        locationSortModeLabel() {
            if (this.layoutMode === 'manual') return 'Свой порядок';
            const direction = this.layoutDirection === 'asc' ? '↑' : '↓';
            return this.layoutMode === 'name'
                ? `По имени ${direction}`
                : `По числу людей ${direction}`;
        },
        locationSortModeIcon() {
            if (this.layoutMode === 'name') {
                return this.layoutDirection === 'asc'
                    ? 'fas fa-sort-alpha-down'
                    : 'fas fa-sort-alpha-down-alt';
            }
            return this.layoutDirection === 'asc'
                ? 'fas fa-sort-numeric-down-alt'
                : 'fas fa-sort-numeric-down';
        },
        selectedFlatObjects() {
            const selected = new Set(this.selectedObjectKeys);
            return this.orderedAllObjectLocations.filter(object => (
                selected.has(object.layout_key)
            ));
        },
        selectedFlatGroups() {
            const selected = new Set(this.selectedGroupKeys);
            return this.groups.filter(group => (
                selected.has(this.groupLayoutKey(group))
            ));
        },
        selectedLocationCount() {
            return this.selectedGroupKeys.length + this.selectedObjectKeys.length;
        },
        usesFlatObjectGrid() {
            return this.selectedScopeKey === 'selection';
        },
        showsLocationLayoutControls() {
            return !this.focusedObject && this.selectedScopeObjectCount > 1;
        },
        visibleFlatObjects() {
            return this.selectedScopeKey === 'selection'
                ? this.selectedFlatObjects
                : [];
        },
        allAssignedEmployeeCount() {
            const employees = [];
            this.groups.forEach(group => {
                employees.push(...this.filtered(group.employees));
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    employees.push(...this.filtered(object.employees));
                });
            });
            this.standaloneObjects.forEach(object => {
                employees.push(...this.filtered(object.employees));
            });
            return this.uniqueEmployeeCount(employees);
        },
        standaloneEmployeeCount() {
            const employees = [];
            this.standaloneObjects.forEach(object => {
                employees.push(...this.filtered(object.employees));
            });
            return this.uniqueEmployeeCount(employees);
        },
        standaloneSelectionState() {
            return this.objectCollectionSelectionState(this.standaloneObjects);
        },
        standaloneSelectionTitle() {
            return this.standaloneSelectionState === 'all'
                ? 'Убрать все объекты без группы из выбранных'
                : 'Добавить все объекты без группы к выбранным';
        },
        normalizedLocationQuery() {
            return String(this.locationQuery || '').trim().toLocaleLowerCase('ru-RU');
        },
        navigationGroups() {
            const query = this.normalizedLocationQuery;
            return this.groups.reduce((result, group) => {
                const objects = Array.isArray(group.objects) ? group.objects : [];
                const groupMatches = !query ||
                    String(group.name || '').toLocaleLowerCase('ru-RU').includes(query);
                const matchingObjects = groupMatches
                    ? objects
                    : objects.filter(object => this.objectMatchesQuery(object, query));
                if (groupMatches || matchingObjects.length) {
                    result.push(Object.assign({}, group, {
                        navigationObjects: this.sortedNavigationObjects(matchingObjects)
                    }));
                }
                return result;
            }, []);
        },
        navigationStandaloneObjects() {
            const query = this.normalizedLocationQuery;
            const objects = !query
                ? this.standaloneObjects
                : this.standaloneObjects.filter(object => (
                this.objectMatchesQuery(object, query)
                ));
            return this.sortedNavigationObjects(objects);
        },
        standaloneBranchVisible() {
            const query = this.normalizedLocationQuery;
            return this.standaloneObjects.length > 0 && (
                !query || 'без группы'.includes(query)
            );
        },
        navigationHasMatches() {
            return this.navigationGroups.length > 0 ||
                this.navigationStandaloneObjects.length > 0 ||
                this.standaloneBranchVisible;
        },
        selectedGroup() {
            if (!String(this.selectedScopeKey).startsWith('group:')) return null;
            const id = String(this.selectedScopeKey).slice('group:'.length);
            return this.groups.find(group => String(group.id) === id) || null;
        },
        focusedObject() {
            if (!String(this.selectedScopeKey).startsWith('object:')) return null;
            const id = String(this.selectedScopeKey).slice('object:'.length);
            return this.allObjects.find(object => String(this.objectId(object)) === id) || null;
        },
        visibleGroups() {
            if (this.selectedScopeKey === 'all') {
                return this.groups;
            }
            return this.selectedGroup ? [this.selectedGroup] : [];
        },
        displayGroups() {
            const objectsByScope = this.orderedAllObjectLocations.reduce(
                (result, object) => {
                    const scopeKey = object.location_scope_key;
                    if (!result[scopeKey]) result[scopeKey] = [];
                    result[scopeKey].push(object);
                    return result;
                },
                {}
            );
            return this.visibleGroups.map(group => {
                const scopeKey = `group:${group.id}`;
                const objects = objectsByScope[scopeKey] || [];
                const filledObjects = objects.filter(object => (
                    this.locationHasEmployees(object)
                ));
                const emptyObjects = objects.filter(object => (
                    !this.locationHasEmployees(object)
                ));
                const expanded = this.expandedEmptyGroups[String(group.id)] === true;
                const displayObjects = filledObjects.slice();
                if (emptyObjects.length) {
                    displayObjects.push({
                        empty_toggle: true,
                        empty_count: emptyObjects.length,
                        expanded,
                        layout_key: `empty-toggle:${group.id}`
                    });
                    if (expanded) displayObjects.push(...emptyObjects);
                }
                return Object.assign({}, group, {
                    layout_scope_key: scopeKey,
                    display_objects: displayObjects,
                    visible_object_count: filledObjects.length +
                        (expanded ? emptyObjects.length : 0)
                });
            });
        },
        overviewGroups() {
            const objectsByScope = this.orderedAllObjectLocations.reduce(
                (result, object) => {
                    const scopeKey = object.location_scope_key;
                    if (!result[scopeKey]) result[scopeKey] = [];
                    result[scopeKey].push(object);
                    return result;
                },
                {}
            );
            return this.groups.map(group => Object.assign({}, group, {
                objects: objectsByScope[`group:${group.id}`] || []
            }));
        },
        overviewStandaloneObjects() {
            return this.orderedAllObjectLocations.filter(object => (
                object.location_scope_key === 'standalone'
            ));
        },
        visibleStandaloneObjects() {
            return (
                this.selectedScopeKey === 'all' ||
                this.selectedScopeKey === 'standalone'
            )
                ? this.orderedAllObjectLocations.filter(object => (
                    object.location_scope_key === 'standalone'
                ))
                : [];
        },
        selectedScopeTitle() {
            if (this.focusedObject) return this.focusedObject.name;
            if (this.selectedGroup) return this.selectedGroup.name;
            if (this.selectedScopeKey === 'selection') return 'Выбранные площадки';
            if (this.selectedScopeKey === 'standalone') return 'Объекты без группы';
            return 'Все площадки';
        },
        selectedScopeSubtitle() {
            if (this.focusedObject) {
                return 'Фокус на одном объекте — удобно для комплектования его команды.';
            }
            if (this.selectedGroup) {
                return 'Объекты одной группы и сотрудники, назначенные на группу целиком.';
            }
            if (this.selectedScopeKey === 'selection') {
                return 'Выбранные группы и объекты показаны рядом для сравнения и расстановки.';
            }
            if (this.selectedScopeKey === 'standalone') {
                return 'Объекты, которые ещё не включены в группу.';
            }
            return 'Каждая группа показана отдельной секцией: место «На группе объектов» ' +
                'и её объекты находятся рядом.';
        },
        selectedScopeObjectCount() {
            if (this.focusedObject) return 1;
            if (this.selectedGroup) {
                return Array.isArray(this.selectedGroup.objects)
                    ? this.selectedGroup.objects.length
                    : 0;
            }
            if (this.selectedScopeKey === 'standalone') {
                return this.standaloneObjects.length;
            }
            if (this.selectedScopeKey === 'selection') {
                return this.selectedFlatObjects.length;
            }
            return this.totalObjectCount;
        },
        selectedScopeEmployeeCount() {
            if (this.focusedObject) return this.employeeCount(this.focusedObject.employees);
            if (this.selectedGroup) return this.groupEmployeeCount(this.selectedGroup);
            if (this.selectedScopeKey === 'standalone') return this.standaloneEmployeeCount;
            if (this.selectedScopeKey === 'selection') {
                const employees = [];
                this.selectedFlatGroups.forEach(group => {
                    employees.push(...this.filtered(group.employees));
                });
                this.selectedFlatObjects.forEach(object => {
                    employees.push(...this.filtered(object.employees));
                });
                return this.uniqueEmployeeCount(employees);
            }
            return this.allAssignedEmployeeCount;
        },
        activeScopeFilter() {
            if (this.selectedScopeKey === 'all') {
                return null;
            }
            let text = `Область: ${this.selectedScopeTitle}`;
            if (this.selectedScopeKey === 'selection') {
                text += ` (${this.selectedLocationCount})`;
            }
            return {
                key: String(this.selectedScopeKey),
                text
            };
        }
    },
    watch: {
        nameFormat(value) {
            this.employeeNameFormatState.value = value;
        },
        layoutStorageKey: {
            immediate: true,
            handler() {
                this.restoreLocationLayout();
            }
        },
        allObjectLocations() {
            const validKeys = new Set(
                this.allObjectLocations.map(object => object.layout_key)
            );
            const validGroupKeys = new Set(
                this.groups.map(group => this.groupLayoutKey(group))
            );
            const selected = this.selectedObjectKeys.filter(key => validKeys.has(key));
            const selectedGroups = this.selectedGroupKeys.filter(key => (
                validGroupKeys.has(key)
            ));
            if (selected.length !== this.selectedObjectKeys.length) {
                this.selectedObjectKeys = selected;
            }
            if (selectedGroups.length !== this.selectedGroupKeys.length) {
                this.selectedGroupKeys = selectedGroups;
            }
            if (
                !selected.length &&
                !selectedGroups.length &&
                this.selectedScopeKey === 'selection'
            ) {
                this.selectedScopeKey = 'all';
            }
            const scopeKey = String(this.selectedScopeKey || 'all');
            if (
                (
                    scopeKey.startsWith('object:') &&
                    !validKeys.has(scopeKey)
                ) ||
                (
                    scopeKey.startsWith('group:') &&
                    !this.groups.some(group => (
                        `group:${group.id}` === scopeKey
                    ))
                ) ||
                (
                    scopeKey === 'standalone' &&
                    !this.standaloneObjects.length
                )
            ) {
                this.selectedScopeKey = 'all';
            }
        },
        activeScopeFilter: {
            immediate: true,
            deep: true,
            handler(value) {
                this.$emit(
                    'scope-change',
                    value ? Object.assign({}, value) : null
                );
            }
        },
        scopeResetToken(value, previousValue) {
            if (value === previousValue) {
                return;
            }
            this.selectedGroupKeys = [];
            this.selectedObjectKeys = [];
            this.selectedScopeKey = 'all';
        }
    },
    methods: {
        captureViewState() {
            const overviewBoard = this.$refs.overviewBoard;
            return {
                selectedScopeKey: this.selectedScopeKey,
                selectedGroupKeys: this.selectedGroupKeys.slice(),
                selectedObjectKeys: this.selectedObjectKeys.slice(),
                locationQuery: this.locationQuery,
                collapsedSections: Object.assign({}, this.collapsedSections),
                collapsedPriorityLanes: Object.assign(
                    {},
                    this.collapsedPriorityLanes
                ),
                expandedEmptyGroups: Object.keys(this.expandedEmptyGroups)
                    .filter(key => this.expandedEmptyGroups[key] === true),
                overview: overviewBoard &&
                    typeof overviewBoard.captureViewState === 'function'
                    ? overviewBoard.captureViewState()
                    : null
            };
        },
        restoreViewState(state) {
            const value = state && typeof state === 'object' ? state : {};
            this.selectedGroupKeys = Array.isArray(value.selectedGroupKeys)
                ? value.selectedGroupKeys.slice()
                : [];
            this.selectedObjectKeys = Array.isArray(value.selectedObjectKeys)
                ? value.selectedObjectKeys.slice()
                : [];
            this.selectedScopeKey = value.selectedScopeKey || 'all';
            this.locationQuery = String(value.locationQuery || '');
            this.collapsedSections = Object.assign({}, value.collapsedSections || {});
            this.collapsedPriorityLanes = Object.assign(
                {},
                value.collapsedPriorityLanes || {}
            );
            this.expandedEmptyGroups = (Array.isArray(
                value.expandedEmptyGroups
            ) ? value.expandedEmptyGroups : []).reduce((result, key) => {
                result[String(key)] = true;
                return result;
            }, {});
            this.$nextTick(() => {
                const overviewBoard = this.$refs.overviewBoard;
                if (
                    overviewBoard &&
                    typeof overviewBoard.restoreViewState === 'function'
                ) {
                    overviewBoard.restoreViewState(value.overview);
                }
            });
        },
        filtered(employees) {
            return this.filterEmployees(Array.isArray(employees) ? employees : []);
        },
        plannedEmployeesForTarget(type, id) {
            const key = `${type}:${id}`;
            const employees = this.plannedEmployeesByTarget[key];
            return Array.isArray(employees) ? employees : [];
        },
        locationHasEmployees(object) {
            const employees = object && Array.isArray(object.employees)
                ? object.employees
                : [];
            return employees.length > 0 || this.plannedEmployeesForTarget(
                'object',
                this.objectId(object)
            ).length > 0;
        },
        toggleGroupEmptyObjects(groupId) {
            const key = String(groupId);
            this.$set(
                this.expandedEmptyGroups,
                key,
                this.expandedEmptyGroups[key] !== true
            );
        },
        employeeCount(employees) {
            return this.filtered(employees).length;
        },
        uniqueEmployeeCount(employees) {
            const seen = {};
            return (Array.isArray(employees) ? employees : []).reduce(
                (count, employee) => {
                    const id = employee && (employee.employee_id || employee.id);
                    const key = id === null || id === undefined
                        ? `anonymous:${employee && (
                            employee.display_name ||
                            employee.full_name ||
                            employee.name
                        ) || ''}`
                        : String(id);
                    if (seen[key]) {
                        return count;
                    }
                    seen[key] = true;
                    return count + 1;
                },
                0
            );
        },
        groupEmployeeCount(group) {
            const source = group || {};
            const employees = this.filtered(source.employees);
            (Array.isArray(source.objects) ? source.objects : []).forEach(object => {
                employees.push(...this.filtered(object.employees));
            });
            return this.uniqueEmployeeCount(employees);
        },
        objectMatchesQuery(object, query) {
            const value = String(query || '');
            return String(object && object.name || '').toLocaleLowerCase('ru-RU').includes(value)
                || String(this.objectId(object) || '').includes(value);
        },
        scopeIsActive(key) {
            return String(this.selectedScopeKey) === String(key);
        },
        selectScope(key) {
            this.selectedScopeKey = key;
        },
        objectLayoutKey(object) {
            const id = this.objectId(object);
            return id ? `object:${id}` : null;
        },
        groupLayoutKey(group) {
            const id = group && group.id;
            return id === null || id === undefined ? null : `group:${id}`;
        },
        objectIsSelected(object) {
            const key = this.objectLayoutKey(object);
            return Boolean(key && this.selectedObjectKeys.indexOf(key) !== -1);
        },
        objectCollectionKeys(objects) {
            return Array.from(new Set(
                (Array.isArray(objects) ? objects : [])
                    .map(object => this.objectLayoutKey(object))
                    .filter(Boolean)
            ));
        },
        objectCollectionSelectionState(objects) {
            const keys = this.objectCollectionKeys(objects);
            if (!keys.length) return 'none';
            const selected = new Set(this.selectedObjectKeys);
            const selectedCount = keys.reduce((count, key) => (
                count + (selected.has(key) ? 1 : 0)
            ), 0);
            if (!selectedCount) return 'none';
            return selectedCount === keys.length ? 'all' : 'some';
        },
        groupSelectionState(group) {
            const groupKey = this.groupLayoutKey(group);
            const groupSelected = Boolean(
                groupKey && this.selectedGroupKeys.indexOf(groupKey) !== -1
            );
            const objectKeys = this.objectCollectionKeys(group && group.objects);
            const selectedObjects = new Set(this.selectedObjectKeys);
            const selectedObjectCount = objectKeys.reduce((count, key) => (
                count + (selectedObjects.has(key) ? 1 : 0)
            ), 0);
            if (groupSelected && selectedObjectCount === objectKeys.length) {
                return 'all';
            }
            return groupSelected || selectedObjectCount ? 'some' : 'none';
        },
        selectionIcon(state) {
            if (state === 'all') return 'fas fa-check-square';
            if (state === 'some') return 'fas fa-minus-square';
            return 'far fa-square';
        },
        selectionAriaPressed(state) {
            if (state === 'some') return 'mixed';
            return state === 'all' ? 'true' : 'false';
        },
        groupSelectionTitle(group) {
            const name = group && group.name || 'Группа объектов';
            return this.groupSelectionState(group) === 'all'
                ? `Убрать группу «${name}» и все её объекты из выбранных`
                : `Добавить группу «${name}» и все её объекты к выбранным`;
        },
        toggleGroupSelection(group) {
            const groupKey = this.groupLayoutKey(group);
            if (!groupKey) return;
            const objectKeys = this.objectCollectionKeys(group && group.objects);
            const selectedGroups = new Set(this.selectedGroupKeys);
            const selectedObjects = new Set(this.selectedObjectKeys);
            const removeSelection = this.groupSelectionState(group) === 'all';
            if (removeSelection) {
                selectedGroups.delete(groupKey);
                objectKeys.forEach(key => selectedObjects.delete(key));
            } else {
                selectedGroups.add(groupKey);
                objectKeys.forEach(key => selectedObjects.add(key));
            }
            this.selectedGroupKeys = this.groups
                .map(item => this.groupLayoutKey(item))
                .filter(key => selectedGroups.has(key));
            this.selectedObjectKeys = this.orderedAllObjectLocations
                .map(object => object.layout_key)
                .filter(key => selectedObjects.has(key));
            this.selectedScopeKey = this.selectedLocationCount
                ? 'selection'
                : 'all';
        },
        toggleObjectCollectionSelection(objects) {
            const keys = this.objectCollectionKeys(objects);
            if (!keys.length) return;
            const selected = new Set(this.selectedObjectKeys);
            const allSelected = keys.every(key => selected.has(key));
            keys.forEach(key => {
                if (allSelected) {
                    selected.delete(key);
                } else {
                    selected.add(key);
                }
            });
            this.selectedObjectKeys = this.orderedAllObjectLocations
                .map(object => object.layout_key)
                .filter(key => selected.has(key));
            this.selectedScopeKey = this.selectedLocationCount
                ? 'selection'
                : 'all';
        },
        objectSelectionTitle(object) {
            return this.objectIsSelected(object)
                ? `Убрать объект «${object.name}» из выбранных`
                : `Показать объект «${object.name}» рядом с выбранными`;
        },
        toggleObjectSelection(object) {
            const key = this.objectLayoutKey(object);
            if (!key) return;
            const selected = this.selectedObjectKeys.slice();
            const index = selected.indexOf(key);
            if (index === -1) {
                selected.push(key);
            } else {
                selected.splice(index, 1);
            }
            this.selectedObjectKeys = selected;
            this.selectedScopeKey = this.selectedLocationCount ? 'selection' : 'all';
        },
        clearObjectSelection() {
            this.selectedGroupKeys = [];
            this.selectedObjectKeys = [];
            if (this.selectedScopeKey === 'selection') {
                this.selectedScopeKey = 'all';
            }
        },
        browserStorage() {
            return typeof window !== 'undefined' ? window.localStorage : null;
        },
        restoreLocationLayout() {
            const stored = workforceLayout.readLayout(
                this.browserStorage(),
                this.currentUser
            );
            this.layoutMode = stored.mode;
            this.manualLocationOrder = stored.order;
            this.stopLocationDrag();
        },
        persistLocationLayout() {
            workforceLayout.saveManualLayout(
                this.browserStorage(),
                this.currentUser,
                this.manualLocationOrder
            );
        },
        resetLocationLayout() {
            workforceLayout.clearLayout(
                this.browserStorage(),
                this.currentUser
            );
            this.layoutMode = 'headcount';
            this.layoutDirection = 'desc';
            this.manualLocationOrder = [];
            this.stopLocationDrag();
        },
        sortedNavigationObjects(objects) {
            if (this.layoutMode === 'manual') {
                const keys = new Set((objects || []).map(object => (
                    this.objectLayoutKey(object)
                )));
                return this.orderedAllObjectLocations.filter(object => (
                    keys.has(object.layout_key)
                ));
            }
            return workforceLayout.sortLocations(
                objects,
                this.layoutMode,
                this.layoutDirection
            );
        },
        setLocationSort(mode) {
            if (mode !== 'name' && mode !== 'headcount') return;
            if (this.layoutMode === mode) {
                this.layoutDirection = this.layoutDirection === 'asc'
                    ? 'desc'
                    : 'asc';
            } else {
                this.layoutMode = mode;
                this.layoutDirection = mode === 'name' ? 'asc' : 'desc';
            }
            this.layoutMode = mode;
            this.manualLocationOrder = [];
            workforceLayout.clearLayout(
                this.browserStorage(),
                this.currentUser
            );
            this.stopLocationDrag();
        },
        locationSortTitle(mode) {
            const active = this.layoutMode === mode;
            const direction = active ? this.layoutDirection : (
                mode === 'name' ? 'asc' : 'desc'
            );
            const nextDirection = active
                ? (direction === 'asc' ? 'desc' : 'asc')
                : direction;
            const subject = mode === 'name'
                ? 'по имени'
                : 'по количеству людей';
            const order = nextDirection === 'asc'
                ? 'по возрастанию'
                : 'по убыванию';
            return `Сортировать ${subject}, ${order}`;
        },
        locationSortDirectionIcon(mode) {
            const active = this.layoutMode === mode;
            const direction = active ? this.layoutDirection : (
                mode === 'name' ? 'asc' : 'desc'
            );
            return direction === 'asc'
                ? 'fas fa-long-arrow-alt-up'
                : 'fas fa-long-arrow-alt-down';
        },
        locationTileClasses(object) {
            return {
                'workforce-location-tile--dragging':
                    this.draggingLocationKey === object.layout_key,
                'workforce-location-tile--drop-before':
                    this.layoutDropTargetKey === object.layout_key &&
                    this.layoutDropPosition === 'before',
                'workforce-location-tile--drop-after':
                    this.layoutDropTargetKey === object.layout_key &&
                    this.layoutDropPosition === 'after'
            };
        },
        startLocationDrag(object, event, scopeKey) {
            if (this.mobile || !object || !object.layout_key) return;
            this.draggingLocationKey = object.layout_key;
            this.draggingLocationScopeKey = scopeKey
                ? String(scopeKey)
                : null;
            this.layoutDropTargetKey = null;
            this.layoutDropPosition = 'before';
            if (event && event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData(
                    'application/x-argus-workforce-location',
                    object.layout_key
                );
                if (this.draggingLocationScopeKey) {
                    event.dataTransfer.setData(
                        'application/x-argus-workforce-location-scope',
                        this.draggingLocationScopeKey
                    );
                }
            }
        },
        stopLocationDrag() {
            this.draggingLocationKey = null;
            this.draggingLocationScopeKey = null;
            this.layoutDropTargetKey = null;
            this.layoutDropPosition = 'before';
        },
        locationScopeAccepts(scopeKey) {
            if (!this.draggingLocationScopeKey) return true;
            return this.draggingLocationScopeKey === String(scopeKey || '');
        },
        locationDropPosition(event) {
            const element = event && event.currentTarget;
            if (!element || typeof element.getBoundingClientRect !== 'function') {
                return 'before';
            }
            const rect = element.getBoundingClientRect();
            const xRatio = rect.width
                ? (event.clientX - rect.left) / rect.width
                : 0;
            const yRatio = rect.height
                ? (event.clientY - rect.top) / rect.height
                : 0;
            if (yRatio < 0.24) return 'before';
            if (yRatio > 0.76) return 'after';
            return xRatio < 0.5 ? 'before' : 'after';
        },
        onLocationDragOver(event, object, scopeKey) {
            if (
                !this.draggingLocationKey ||
                !object ||
                object.empty_toggle === true
            ) {
                return;
            }
            if (!this.locationScopeAccepts(scopeKey)) {
                event.preventDefault();
                event.stopPropagation();
                if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = 'none';
                }
                this.layoutDropTargetKey = null;
                return;
            }
            if (object.layout_key === this.draggingLocationKey) return;
            event.preventDefault();
            event.stopPropagation();
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'move';
            }
            this.layoutDropTargetKey = object.layout_key;
            this.layoutDropPosition = this.locationDropPosition(event);
        },
        onLocationDragLeave(event, object) {
            if (
                !object ||
                object.empty_toggle === true ||
                this.layoutDropTargetKey !== object.layout_key ||
                (
                    event.currentTarget &&
                    event.relatedTarget &&
                    event.currentTarget.contains(event.relatedTarget)
                )
            ) {
                return;
            }
            this.layoutDropTargetKey = null;
        },
        onLocationDrop(event, object, scopeKey) {
            if (
                !this.draggingLocationKey ||
                !object ||
                object.empty_toggle === true
            ) return;
            event.preventDefault();
            event.stopPropagation();
            if (!this.locationScopeAccepts(scopeKey)) {
                this.stopLocationDrag();
                return;
            }
            const currentOrder = this.orderedAllObjectLocations.map(item => (
                item.layout_key
            ));
            const nextOrder = workforceLayout.reorderLocationKeys(
                currentOrder,
                this.draggingLocationKey,
                object.layout_key,
                this.layoutDropPosition
            );
            if (nextOrder.join('|') !== currentOrder.join('|')) {
                this.layoutMode = 'manual';
                this.manualLocationOrder = nextOrder;
                this.persistLocationLayout();
            }
            this.stopLocationDrag();
        },
        moveLocationByStep(locationKey, step, scopeKey) {
            if (this.mobile || !locationKey || !step) return;
            const visibleLocations = scopeKey
                ? this.orderedAllObjectLocations.filter(object => (
                    object.location_scope_key === String(scopeKey)
                ))
                : this.visibleFlatObjects;
            const visibleOrder = visibleLocations.map(item => (
                item.layout_key
            ));
            const sourceIndex = visibleOrder.indexOf(locationKey);
            const targetIndex = sourceIndex + Number(step);
            if (
                sourceIndex === -1 ||
                targetIndex < 0 ||
                targetIndex >= visibleOrder.length
            ) {
                return;
            }
            const currentOrder = this.orderedAllObjectLocations.map(item => (
                item.layout_key
            ));
            const nextOrder = workforceLayout.reorderLocationKeys(
                currentOrder,
                locationKey,
                visibleOrder[targetIndex],
                step < 0 ? 'before' : 'after'
            );
            if (nextOrder.join('|') !== currentOrder.join('|')) {
                this.layoutMode = 'manual';
                this.manualLocationOrder = nextOrder;
                this.persistLocationLayout();
            }
        },
        sectionIsCollapsed(key) {
            if (String(this.searchQuery || '').trim()) {
                return false;
            }
            return this.collapsedSections[key] === true;
        },
        toggleSection(key) {
            this.$set(this.collapsedSections, key, !this.sectionIsCollapsed(key));
        },
        priorityLaneIsCollapsed(key) {
            if (this.searchIsActive) {
                return false;
            }
            return this.collapsedPriorityLanes[key] === true;
        },
        togglePriorityLane(key) {
            this.$set(
                this.collapsedPriorityLanes,
                key,
                !this.priorityLaneIsCollapsed(key)
            );
        },
        forwardContextMenu(event, employee) {
            this.$emit('contextmenu', event, employee);
        }
    }
};
</script>

<style scoped>
.workforce-grid-shell {
    min-width: 0;
}

.workforce-priority-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 12px;
    margin-bottom: 12px;
}

.workforce-priority-grid >>> .workforce-lane__cards {
    max-height: 260px;
}

.workforce-reserve-tray {
    margin-bottom: 12px;
    padding: 10px;
    border: 1px solid #d7d1c5;
    border-radius: 11px;
    background: #f8f6f1;
}

.workforce-reserve-tray__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 2px 9px;
    color: #4f4b43;
}

.workforce-reserve-tray__header > div {
    display: flex;
    align-items: center;
    gap: 7px;
}

.workforce-reserve-tray__header span {
    color: #776f62;
    font-size: 0.76rem;
}

.workforce-reserve-tray__header button {
    width: 30px;
    height: 30px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #776f62;
}

.workforce-reserve-tray__header button:hover,
.workforce-reserve-tray__header button:focus {
    background: #ebe5da;
}

.workforce-reserve-tray >>> .workforce-lane {
    border-color: #d7d1c5;
    background: #fffdf9;
}

.workforce-reserve-tray >>> .workforce-lane__cards {
    max-height: 330px;
}

.workforce-grid-workspace {
    display: grid;
    grid-template-columns: 238px minmax(0, 1fr);
    align-items: start;
    gap: 12px;
    min-width: 0;
    transition: grid-template-columns 0.18s ease, gap 0.18s ease;
}

.workforce-grid-workspace--scope-collapsed {
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 8px;
}

.workforce-scope {
    position: sticky;
    top: calc(
        var(--shell-header-height, 64px) +
        var(--workforce-toolbar-height, 60px) +
        16px
    );
    min-width: 0;
    overflow: hidden;
    border: 1px solid #dce3ea;
    border-radius: 11px;
    background: #fff;
}

.workforce-scope--collapsed {
    border-color: #cbd8e4;
    background: #f5f8fb;
}

.workforce-scope__collapsed-strip {
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 9px;
    width: 100%;
    min-height: 190px;
    padding: 10px 6px;
    border: 0;
    background: transparent;
    color: #52708d;
}

.workforce-scope__collapsed-strip:hover,
.workforce-scope__collapsed-strip:focus {
    background: #e7f1fa;
    color: #2468a8;
}

.workforce-scope__collapsed-strip > i {
    font-size: 0.78rem;
}

.workforce-scope__collapsed-strip > span {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.workforce-scope__collapsed-strip > strong {
    min-width: 24px;
    padding: 2px 4px;
    border-radius: 999px;
    background: #dfeaf4;
    font-size: 0.66rem;
    text-align: center;
}

.workforce-scope__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 12px 9px;
    border-bottom: 1px solid #e5eaf0;
}

.workforce-scope__heading > div:first-child {
    display: flex;
    flex-direction: column;
}

.workforce-scope__heading strong {
    color: #2b3a49;
    font-size: 0.86rem;
}

.workforce-scope__heading span {
    color: #7b8895;
    font-size: 0.7rem;
}

.workforce-scope__sorts {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 3px;
}

.workforce-scope__sort {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    width: 30px;
    height: 27px;
    padding: 0;
    border: 1px solid #d7e0e8;
    border-radius: 6px;
    background: #fff;
    color: #7a8d9f;
    font-size: 0.67rem;
}

.workforce-scope__sort i:last-child {
    font-size: 0.58rem;
}

.workforce-scope__sort:hover,
.workforce-scope__sort:focus {
    border-color: #8eb5dc;
    background: #f4f8fc;
    color: #376faa;
}

.workforce-scope__sort--active {
    border-color: #7aa9d8;
    background: #eaf3fc;
    color: #2467aa;
    box-shadow: inset 0 0 0 1px rgba(36, 103, 170, 0.08);
}

.workforce-scope__collapse {
    border-color: #b8c8d7;
    color: #536f89;
}

.workforce-scope__search {
    padding: 9px;
}

.workforce-scope__nav {
    max-height: min(66vh, 720px);
    overflow-y: auto;
    padding: 0 7px 9px;
    overscroll-behavior-y: auto;
}

.workforce-scope__branch {
    margin-top: 3px;
}

.workforce-scope__selection {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 30px;
    gap: 2px;
    margin: 2px 0 6px;
    padding-bottom: 6px;
    border-bottom: 1px solid #edf1f5;
}

.workforce-scope__selection-clear {
    width: 30px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #8493a1;
}

.workforce-scope__selection-clear:hover,
.workforce-scope__selection-clear:focus {
    background: #fbeaea;
    color: #a43b3b;
}

.workforce-scope__group-row,
.workforce-scope__object-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 28px;
    align-items: center;
    gap: 1px;
    border-radius: 7px;
}

.workforce-scope__group-row--selected,
.workforce-scope__object-row--selected {
    background: #f0f7fe;
}

.workforce-scope__group-toggle,
.workforce-scope__object-toggle {
    width: 28px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #9aa8b5;
    font-size: 0.78rem;
}

.workforce-scope__group-toggle:hover,
.workforce-scope__group-toggle:focus,
.workforce-scope__object-toggle:hover,
.workforce-scope__object-toggle:focus {
    background: #e7f1fb;
    color: #2e6fae;
}

.workforce-scope__group-toggle--selected,
.workforce-scope__object-toggle--selected {
    color: #1769b0;
}

.workforce-scope__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-width: 0;
    padding: 7px 8px;
    border: 0;
    border-radius: 7px;
    background: transparent;
    color: #536577;
    font-size: 0.76rem;
    text-align: left;
}

.workforce-scope__item:hover,
.workforce-scope__item:focus {
    background: #eef4fa;
    color: #285f96;
}

.workforce-scope__item--active {
    background: #e4f0fc;
    color: #185d9f;
    font-weight: 700;
}

.workforce-scope__item > span {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 7px;
}

.workforce-scope__item > span i {
    flex: 0 0 14px;
    width: 14px;
    color: #7890a8;
    text-align: center;
}

.workforce-scope__item > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-scope__item > strong {
    flex: 0 0 auto;
    min-width: 22px;
    padding: 1px 5px;
    border-radius: 999px;
    background: rgba(105, 129, 153, 0.1);
    font-size: 0.68rem;
    text-align: center;
}

.workforce-scope__item--root {
    margin-bottom: 4px;
    border-bottom: 1px solid #edf1f5;
    border-radius: 7px 7px 0 0;
    font-weight: 700;
}

.workforce-scope__item--group {
    font-weight: 650;
}

.workforce-scope__item--object {
    padding-left: 25px;
    font-size: 0.72rem;
}

.workforce-scope__empty {
    padding: 20px 8px;
    color: #8a96a2;
    font-size: 0.76rem;
    text-align: center;
}

.workforce-locations {
    min-width: 0;
}

.workforce-locations__header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 10px;
    padding: 3px 2px 0;
}

.workforce-locations__eyebrow {
    color: #7690aa;
    font-size: 0.67rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.workforce-locations__header h2 {
    margin: 1px 0 0;
    color: #2c4054;
    font-size: 1.12rem;
    font-weight: 700;
}

.workforce-locations__header p {
    margin: 2px 0 0;
    color: #788695;
    font-size: 0.75rem;
}

.workforce-locations__header-actions {
    display: flex;
    align-items: flex-end;
    flex: 0 0 auto;
    flex-direction: column;
    gap: 6px;
}

.workforce-layout-mode {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    min-height: 28px;
}

.workforce-layout-mode__status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 7px;
    border-radius: 999px;
    background: #edf3f8;
    color: #5c7287;
    font-size: 0.69rem;
    font-weight: 700;
    white-space: nowrap;
}

.workforce-layout-mode__status--manual {
    background: #e8f1fb;
    color: #245f99;
}

.workforce-layout-mode__reset {
    padding: 4px 7px;
    border: 1px solid #cbd9e7;
    border-radius: 6px;
    background: #fff;
    color: #376b9e;
    font-size: 0.69rem;
    font-weight: 700;
}

.workforce-layout-mode__reset:hover,
.workforce-layout-mode__reset:focus {
    border-color: #8cb5de;
    background: #f2f7fc;
}

.workforce-layout-mode__hint {
    color: #82909d;
    font-size: 0.68rem;
    white-space: nowrap;
}

.workforce-locations__metrics {
    display: flex;
    align-items: center;
    gap: 6px;
}

.workforce-locations__metrics span {
    padding: 5px 8px;
    border: 1px solid #dfe6ed;
    border-radius: 7px;
    background: #fff;
    color: #6b7b8a;
    font-size: 0.7rem;
    white-space: nowrap;
}

.workforce-locations__metrics strong {
    color: #314c67;
}

.workforce-location-section {
    margin-bottom: 16px;
    overflow: hidden;
    border: 2px solid #b8c8d7;
    border-radius: 13px;
    background: #eaf1f7;
    box-shadow: 0 4px 13px rgba(37, 57, 78, 0.08);
}

.workforce-location-section--group {
    border-color: #9fb5ca;
    background: #e5eef6;
}

.workforce-location-section--standalone {
    border-color: #c0c8d1;
    background: #f0f2f5;
}

.workforce-location-section--flat {
    overflow: visible;
    border: 0;
    background: transparent;
    box-shadow: none;
}

.workforce-location-section--focused {
    padding: 11px;
    border-color: #9eb6cc;
}

.workforce-location-section--collapsed {
    background: #f7f9fb;
    box-shadow: 0 2px 7px rgba(37, 57, 78, 0.05);
}

.workforce-location-section__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-bottom: 2px solid #b8c8d7;
    background: #dce8f2;
    color: #2d4964;
    text-align: left;
}

.workforce-location-section__header:hover,
.workforce-location-section__header:focus {
    background: #d4e3ef;
}

.workforce-location-section--collapsed .workforce-location-section__header {
    border-bottom: 0;
}

.workforce-location-section--group .workforce-location-section__header {
    border-bottom-color: #9fb5ca;
    background: #d5e4f0;
}

.workforce-location-section--group .workforce-location-section__header:hover,
.workforce-location-section--group .workforce-location-section__header:focus {
    background: #ccdeec;
}

.workforce-location-section--standalone .workforce-location-section__header {
    background: #e6e9ed;
}

.workforce-location-section__identity {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
}

.workforce-location-section__identity > i {
    color: #6483a2;
}

.workforce-location-section__identity > span {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.workforce-location-section__identity strong {
    overflow: hidden;
    font-size: 0.82rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workforce-location-section__identity small {
    margin-top: 1px;
    color: #718397;
    font-size: 0.67rem;
}

.workforce-location-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(270px, 100%), 1fr));
    align-items: start;
    gap: 12px;
    padding: 12px;
}

.workforce-location-grid--focused {
    grid-template-columns: minmax(0, 1fr);
    padding: 0;
}

.workforce-location-grid--flat {
    grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
    padding: 0;
}

.workforce-location-tile {
    position: relative;
    min-width: 0;
    padding: 3px;
    border: 1px solid #b7c5d2;
    border-radius: 12px;
    background: #e5ecf2;
    box-shadow: 0 3px 10px rgba(37, 57, 78, 0.09);
    transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.workforce-location-tile--empty-toggle {
    align-self: start;
    padding: 6px;
    border-style: dashed;
    border-color: #8faec8;
    background: #eef4f9;
    box-shadow: none;
}

.workforce-empty-objects-toggle {
    display: flex;
    width: 100%;
    min-height: 78px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 5px;
    padding: 9px;
    border: 1px dashed #7599b8;
    border-radius: 7px;
    color: #28628f;
    background: rgba(255, 255, 255, 0.82);
    font-size: 0.76rem;
    font-weight: 800;
    line-height: 1.15;
}

.workforce-empty-objects-toggle small {
    display: inline-flex;
    min-width: 24px;
    height: 19px;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border-radius: 999px;
    color: #536b82;
    background: #dfe9f2;
    font-size: 0.65rem;
}

.workforce-empty-objects-toggle:hover,
.workforce-empty-objects-toggle:focus {
    border-color: #3979ad;
    background: #e5f1fb;
}

.workforce-location-tile--dragging {
    opacity: 0.48;
    transform: scale(0.985);
}

.workforce-location-tile--drop-before {
    box-shadow: -5px 0 0 #2f80c8;
}

.workforce-location-tile--drop-after {
    box-shadow: 5px 0 0 #2f80c8;
}

.workforce-location-grid >>> .workforce-lane {
    border-color: #b5c4d2;
    background: #f4f7fa;
    box-shadow: 0 3px 9px rgba(36, 55, 74, 0.1);
}

.workforce-location-grid >>> .workforce-object-lane .workforce-lane__header {
    background: #fff;
}

.workforce-location-grid >>> .workforce-group-target-lane {
    border-color: #6f9bc2;
    background: #edf6fd;
    box-shadow: 0 4px 12px rgba(43, 102, 154, 0.14);
}

.workforce-location-grid
    >>> .workforce-group-target-lane .workforce-lane__header {
    border-bottom-color: #9dbbd5;
    background: #dcecf9;
}

.workforce-location-grid >>> .workforce-employee-card {
    border-color: #c5d1dc;
    box-shadow: 0 2px 5px rgba(34, 52, 70, 0.09);
}

.workforce-grid-shell--employee-dragging
    >>> .workforce-lane--drop-enabled {
    border-color: #4d94ce;
    border-style: dashed;
    background: #eff7ff;
    box-shadow:
        inset 0 0 0 1px rgba(40, 120, 199, 0.16),
        0 4px 12px rgba(40, 120, 199, 0.14);
}

.workforce-grid-shell--employee-dragging
    >>> .workforce-lane.workforce-lane--drop-active {
    position: relative;
    z-index: 3;
    border-color: #096fc5;
    border-style: solid;
    background: #dceeff;
    box-shadow:
        inset 0 0 0 3px rgba(9, 111, 197, 0.2),
        0 0 0 3px rgba(9, 111, 197, 0.14),
        0 8px 20px rgba(26, 91, 146, 0.22);
}

.workforce-grid-shell--employee-dragging
    >>> .workforce-lane.workforce-lane--drop-active::after {
    position: absolute;
    z-index: 5;
    right: 10px;
    bottom: 10px;
    padding: 5px 8px;
    border-radius: 6px;
    background: #096fc5;
    color: #fff;
    content: "Отпустите здесь";
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    pointer-events: none;
}

.workforce-location-grid >>> .workforce-lane__cards {
    max-height: min(54vh, 540px);
}

.workforce-planned-object-grid {
    display: grid;
    margin: 0 0 12px;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 10px;
}

.workforce-planned-object-grid >>> .workforce-lane {
    border: 2px dashed #2e8b61;
    background: #f3fbf6;
}

.workforce-planned-object-grid >>> .workforce-lane__header {
    background: #e4f4ea;
}

.workforce-locations__empty {
    display: flex;
    min-height: 220px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 5px;
    border: 1px dashed #d3dce5;
    border-radius: 11px;
    background: #fafbfd;
    color: #7d8b98;
    text-align: center;
}

.workforce-locations__empty i {
    margin-bottom: 4px;
    color: #9cabb9;
    font-size: 1.6rem;
}

.workforce-locations__empty span {
    font-size: 0.75rem;
}

@media (max-width: 991.98px) {
    .workforce-grid-workspace {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .workforce-grid-workspace--scope-collapsed {
        grid-template-columns: 42px minmax(0, 1fr);
    }

    .workforce-location-grid {
        grid-template-columns: minmax(0, 1fr);
    }

}

@media (max-width: 767.98px) {
    .workforce-priority-grid,
    .workforce-grid-workspace {
        grid-template-columns: 1fr;
    }

    .workforce-scope {
        position: static;
    }

    .workforce-grid-workspace--scope-collapsed {
        grid-template-columns: 1fr;
    }

    .workforce-scope--collapsed {
        border-color: #dce3ea;
        background: #fff;
    }

    .workforce-scope__nav {
        max-height: none;
        overflow-y: visible;
        touch-action: pan-y;
    }

    .workforce-locations__header {
        align-items: stretch;
        flex-direction: column;
    }

    .workforce-locations__header-actions {
        align-items: stretch;
    }

    .workforce-layout-mode {
        justify-content: flex-start;
        flex-wrap: wrap;
    }

    .workforce-locations__metrics {
        flex-wrap: wrap;
    }

    .workforce-reserve-tray__header > div {
        align-items: flex-start;
        flex-direction: column;
    }

    .workforce-reserve-tray__header > div > i {
        display: none;
    }
}
</style>
